import { CameraAlt, Close } from "@mui/icons-material"
import { Avatar, Backdrop, Button, CircularProgress, FormLabel, Grid, IconButton, Switch, TextField, Typography } from "@mui/material"
import { Box } from "@mui/system"
import { useMediaContext } from "context/MediaContext"
import React, { useState } from "react"

import { User } from "../../model/User"
import * as userRepository from '../../repository/UserRepository'
import { removeUserProfileImage, uploadUserProfileImage } from "../../script/storage-client"

type Props = {
  user: User
  handleSave: (user: User) => void
  handleCancel: () => void
}
export default function MyPageEditor(props: Props) {
  const { isPc } = useMediaContext()
  const [user, setUser] = useState(props.user)

  /**
   * 画像選択周りの制御
   */
  const [profileImage, setProfileImage] = useState<File | null>(null)
  async function handleSelectImage(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.files) return

    const file = event.target.files.item(0)
    if (!file) return

    setProfileImage(file)
  }

  function handleRemoveProfileImage() {
    if (profileImage) setProfileImage(null)
    else {
      const _user = Object.assign({}, user)
      _user.profile_image_url = undefined
      setUser(_user)
    }
  }

  function getDisplayImageUrl() {
    if (profileImage) return URL.createObjectURL(profileImage)
    else if (user.profile_image_url) return user.profile_image_url
    else return ''
  }

  /**
   * プロフィール情報
   */
  const [userName, setUserName] = useState(props.user.name)
  const [twitterId, setTwitterId] = useState(props.user.twitter_id)
  const [masterDuelId, setMasterDuelId] = useState(props.user.master_duel_id)
  const [openGameHistory, setOpenGameHistory] = useState(props.user.open_game_history)
  const [openWinrateByDecktype, setOpenWinrateByDecktype] = useState(props.user.open_winrate_by_decktype)
  const [openRateTransition, setOpenRateTransition] = useState(props.user.open_rate_transition)
  const [isSaving, setIsSaving] = useState(false)
  async function handleSave() {
    setIsSaving(true)
    const _user = Object.assign({}, user)
    if (profileImage) {
      await removeUserProfileImage(_user.uuid)
      const url = await uploadUserProfileImage(_user.uuid, profileImage)
      _user.profile_image_url = url
    }
    _user.name = userName
    _user.twitter_id = twitterId
    _user.master_duel_id = masterDuelId
    _user.open_game_history = openGameHistory
    _user.open_winrate_by_decktype = openWinrateByDecktype
    _user.open_rate_transition = openRateTransition
    await userRepository.save(_user)
    setIsSaving(false)
    props.handleSave(_user)
  }

  function handleCancel() {
    props.handleCancel()
  }

  
  const [isValidUserName, setIsValidUserName] = useState(true)
  function handleChangeUserName(event: React.ChangeEvent<HTMLInputElement>) {
    setUserName(event.target.value)
    setIsValidUserName(event.target.validity.valid)
  }

  const [isValidTwitterId, setIsValidTwitterId] = useState(true)
  function handleChangeTwitterId(event: React.ChangeEvent<HTMLInputElement>) {
    setTwitterId(event.target.value)
    setIsValidTwitterId(event.target.validity.valid)
  }

  const [isValidMasterDuelId, setIsValidMasterDuelId] = useState(true)
  function handleChangeMasterDuelId(event: React.ChangeEvent<HTMLInputElement>) {
    setIsValidMasterDuelId(event.target.validity.valid)
    setMasterDuelId(event.target.value)
  }

  return (
    <>
      <Grid container spacing={{ xs: 2, lg: 5 }} sx={isPc ? {pt: 2, px: 4} : { pt: 2, px: 2 }}>
        <Grid item xs={4} textAlign='left'>
          <Button
            color='inherit'
            onClick={handleCancel}
          >
            キャンセル
          </Button>
        </Grid>
        <Grid item xs={4} textAlign='center'>
          <Typography fontWeight='bold'>編集</Typography>
        </Grid>
        <Grid item xs={4} textAlign='right'>
          <Button
            color='inherit'
            onClick={handleSave}
            disabled={!isValidTwitterId || !isValidUserName || !isValidMasterDuelId}
          >
            保存
          </Button>
        </Grid>
        <Grid item xs={12} sx={{pb: 3}}>
          <Box sx={{
            position: 'relative',
            height: 100,
            width: 100
          }}>
            <Avatar
              src={getDisplayImageUrl()}
              sx={{
                height: 100,
                width: 100,
                filter: 'brightness(60%)'
              }}
            />
            <label htmlFor='upload-button-profile-image'>
              <input
                id='upload-button-profile-image'
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                onChange={handleSelectImage}
              />
              <IconButton
                component='span'
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
              >
                <CameraAlt />
              </IconButton>
            </label>
            <IconButton
              size='small'
              component='span'
              sx={{
                position: 'absolute',
                top: '10%',
                left: '90%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }}
              onClick={handleRemoveProfileImage}
            >
              <Close />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='ユーザー名'
            fullWidth
            required
            inputProps={{ maxLength: 30}}
            value={userName}
            onChange={handleChangeUserName}
          />
          {!isValidUserName &&
            <Typography variant='body2' color='error.dark'>
              ユーザー名は入力必須です。
            </Typography>
          }
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='Twitter ID（@を含めない）'
            fullWidth
            value={twitterId}
            onChange={handleChangeTwitterId}
          />
          {!isValidTwitterId &&
            <Typography variant='body2' color='error.dark'>
            </Typography>
          }
        </Grid>
        <Grid item xs={12}>
          <TextField
            type='text'
            inputProps={{ inputMode: 'numeric', maxLength: 9, pattern: '[0-9]{9}'}}
            label='Master Duel ID'
            fullWidth
            value={masterDuelId}
            onChange={handleChangeMasterDuelId}
          />
          {!isValidMasterDuelId &&
            <Typography variant='body2' color='error.dark'>
              MasterDuel IDは9桁の数値で入力してください。
            </Typography>
          }
        </Grid>
        <Grid item xs={12}>
          <Switch
            checked={openGameHistory}
            onChange={(e) => setOpenGameHistory(e.target.checked)}
          />
          <FormLabel>対戦履歴を公開する</FormLabel>
        </Grid>
        <Grid item xs={12}>
          <Switch
            checked={openWinrateByDecktype}
            onChange={(e) => setOpenWinrateByDecktype(e.target.checked)}
          />
          <FormLabel>デッキごとの戦績を公開する</FormLabel>
        </Grid>
        <Grid item xs={12}>
          <Switch
            checked={openRateTransition}
            onChange={(e) => setOpenRateTransition(e.target.checked)}
          />
          <FormLabel>レート推移を公開する</FormLabel>
        </Grid>
      </Grid>

      <Backdrop open={isSaving}>
        <CircularProgress />
      </Backdrop>
    </>
  )
}